/** Integer split rules shared by client and server, independent of storage. */
import { isCurrencyCode, MAX_MINOR_INPUT } from "./currencies";

export type SplitMode = "even" | "shares" | "exact" | "percent";

export interface SplitEntry {
  memberId: string;
  value: number;
}

export const BASIS_POINTS_TOTAL = 10_000;
const EVEN_INCLUDED = 1;
const EVEN_EXCLUDED = 0;

export type SplitValidationReason =
  | "invalid_mode" | "invalid_currency" | "invalid_amount" | "amount_too_large" | "unknown_member" | "duplicate_member"
  | "invalid_value" | "invalid_even_value" | "no_participants" | "zero_shares"
  | "exact_total" | "percent_total";

export type SplitValidationResult =
  | { ok: true }
  | { ok: false; reason: SplitValidationReason; detail: string };

export interface SplitValidationInput {
  mode: string;
  amountMinor: number;
  currency: string;
  entries: readonly SplitEntry[];
  memberIds: readonly string[];
}

function reject(reason: SplitValidationReason, detail: string): SplitValidationResult {
  return { ok: false, reason, detail };
}

/** Non-negative integers can be compared to a bounded total without overflowing a sum. */
function matchesTotal(entries: readonly SplitEntry[], total: number): boolean {
  let remaining = total;
  for (const entry of entries) {
    if (entry.value > remaining) return false;
    remaining -= entry.value;
  }
  return remaining === 0;
}

export function validateSplit({ mode, amountMinor, currency, entries, memberIds }: SplitValidationInput): SplitValidationResult {
  if (mode !== "even" && mode !== "shares" && mode !== "exact" && mode !== "percent") {
    return reject("invalid_mode", `Expected even, shares, exact or percent; received ${mode}.`);
  }
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) {
    return reject("invalid_amount", "Expense amount must be a non-negative safe integer of minor units.");
  }
  if (!isCurrencyCode(currency)) return reject("invalid_currency", `Unsupported currency code: ${currency}.`);
  if (amountMinor > MAX_MINOR_INPUT) {
    return reject("amount_too_large", `Expense amount must be at most ${String(MAX_MINOR_INPUT)} ${currency} minor units.`);
  }
  const known = new Set(memberIds);
  const seen = new Set<string>();
  for (const entry of entries) {
    if (!known.has(entry.memberId)) return reject("unknown_member", `Member ${entry.memberId} is not a group member.`);
    if (seen.has(entry.memberId)) return reject("duplicate_member", `Member ${entry.memberId} appears more than once.`);
    seen.add(entry.memberId);
    if (!Number.isSafeInteger(entry.value) || entry.value < 0) {
      return reject("invalid_value", `Value for ${entry.memberId} must be a non-negative safe integer.`);
    }
    if (mode === "exact" && entry.value > MAX_MINOR_INPUT) {
      return reject("amount_too_large", `Exact value for ${entry.memberId} must be at most ${String(MAX_MINOR_INPUT)} ${currency} minor units.`);
    }
  }
  switch (mode) {
    case "even":
      if (entries.some(entry => entry.value !== EVEN_EXCLUDED && entry.value !== EVEN_INCLUDED)) {
        return reject("invalid_even_value", "Even entries must be 0 (excluded) or 1 (included).");
      }
      if (!entries.some(entry => entry.value === EVEN_INCLUDED)) return reject("no_participants", "Select at least one participant.");
      break;
    case "shares":
      if (!entries.some(entry => entry.value > 0)) return reject("zero_shares", "Total share weight must be greater than zero.");
      break;
    case "exact":
      if (!matchesTotal(entries, amountMinor)) return reject("exact_total", `Exact minor-unit shares must sum to ${String(amountMinor)}.`);
      break;
    case "percent":
      if (!matchesTotal(entries, BASIS_POINTS_TOTAL)) return reject("percent_total", `Basis points must sum to ${String(BASIS_POINTS_TOTAL)}.`);
      break;
  }
  return { ok: true };
}

export interface TransferValidationInput {
  from: string;
  to: string;
  amountMinor: number;
  memberIds: readonly string[];
}

export type TransferValidationReason = "invalid_amount" | "amount_too_large" | "unknown_member" | "self_transfer";
export type TransferValidationResult =
  | { ok: true }
  | { ok: false; reason: TransferValidationReason; detail: string };

/** Base-currency payments obey the same input ceiling as split expenses. */
export function validateTransfer({ from, to, amountMinor, memberIds }: TransferValidationInput): TransferValidationResult {
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) {
    return { ok: false, reason: "invalid_amount", detail: "Transfer amount must be a non-negative safe integer of base minor units." };
  }
  if (amountMinor > MAX_MINOR_INPUT) {
    return { ok: false, reason: "amount_too_large", detail: `Transfer amount must be at most ${String(MAX_MINOR_INPUT)} base minor units.` };
  }
  const known = new Set(memberIds);
  for (const id of [from, to]) {
    if (!known.has(id)) return { ok: false, reason: "unknown_member", detail: `Member ${id} is not a group member.` };
  }
  if (from === to) return { ok: false, reason: "self_transfer", detail: "Choose two different group members for a transfer." };
  return { ok: true };
}
